import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { AlertContent } from "@redotech/redo-merchant-app-common/alert";
import { AuthRequired } from "@redotech/redo-merchant-app-common/auth";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  TeamAndUserRequired,
  TeamContext,
} from "@redotech/redo-merchant-app-common/team";
import { UserCacheContext } from "@redotech/redo-merchant-app-common/user-cache";
import { VoiceWidget } from "@redotech/redo-merchant-support-app/voice/voice-widget";
import { GeneralPermissions } from "@redotech/redo-model/permissions";
import { AiSlidePanelLayout } from "@redotech/redo-web/ai/chat/components/ai-slide-panel-layout";
import { ErrorBoundary } from "@redotech/redo-web/error-page";
import { Page as CommonPage } from "@redotech/redo-web/page";
import { memo, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { useEffect_conversionNotifications } from "../conversion/common/hooks/use-conversion-notifications";
import { DataServices } from "../services/data-services";
import { NotificationBanner } from "./banner/banner";
import { GlobalAI } from "./navigation/side-navigation/global-ai";
import { MerchantAppSideNav } from "./navigation/side-navigation/merchant-side-navigation";

function urlIsInSettings(url: string): boolean {
  const matchesStoreUrl = matchPath("/stores/:storeId/settings/*", url);
  const matchesSettingsRoute = matchPath("/settings/*", url);
  return !!(matchesStoreUrl || matchesSettingsRoute);
}

export const Page = memo(function Page({
  hideHeader = () => false,
  hideNavbar = () => false,
  hidePadding = (url: string) => url.includes("ai-builder"),
}: {
  hideHeader?: (url: string, search: { search: string }) => boolean;
  hideNavbar?: (url: string) => boolean;
  hidePadding?: (url: string, search: { search: string }) => boolean;
}) {
  return (
    <AuthRequired>
      <TeamAndUserRequired>
        <DataServices>
          <UserCacheContext.Provider value={new Map()}>
            <PageWithContext
              hideHeader={hideHeader}
              hideNavbar={hideNavbar}
              hidePadding={hidePadding}
            />
          </UserCacheContext.Provider>
        </DataServices>
      </TeamAndUserRequired>
    </AuthRequired>
  );
});

const PageWithContext = memo(function PageWithContext({
  hideHeader = () => false,
  hideNavbar = () => false,
  hidePadding = (url: string) => url.includes("ai-builder"),
}: {
  hideHeader?: (url: string, search: { search: string }) => boolean;
  hideNavbar?: (url: string) => boolean;
  hidePadding?: (url: string, search: { search: string }) => boolean;
}) {
  const team = useRequiredContext(TeamContext);

  const [lastNonSettingsUrl, setLastNonSettingsUrl] = useState<
    string | undefined
  >(undefined);

  const location = useLocation();

  const stableHideNavbar = useHandler(hideNavbar);

  const stableHidePadding = useHandler(hidePadding);

  const shouldHidePadding = useMemo(() => {
    return stableHidePadding(location.pathname, { search: location.search });
  }, [location, stableHidePadding]);

  const hideNavigation = useMemo(() => {
    return stableHideNavbar(location.pathname);
  }, [location, stableHideNavbar]);

  useEffect(() => {
    if (!urlIsInSettings(location.pathname)) {
      setLastNonSettingsUrl(location.pathname);
    }
  }, [location]);

  const routes = useRequiredContext(MerchantRoutesContext);
  const canManageUsers = useHasPermission(GeneralPermissions.MANAGE_TEAM_USERS);

  const settingsUrl = canManageUsers
    ? routes.getSettingsSubRoute("users")
    : routes.getSettingsSubRoute("personal/profile");

  const nonSettingsUrl = lastNonSettingsUrl || `/stores/${team._id}/summary`;
  const settingsRoot = `/stores/${team._id}/settings`;

  const intercom = useIntercom();

  useEffect_conversionNotifications();

  return (
    <AiSlidePanelLayout panel={<GlobalAI />} panelWidth="sm">
      <CommonPage
        banner={<NotificationBanner />}
        ErrorBoundary={ErrorBoundary}
        hideHeader={hideHeader}
        hidePadding={shouldHidePadding}
        nav={
          <MerchantAppSideNav
            enterSettingsUrl={settingsUrl}
            exitSettingsUrl={nonSettingsUrl}
            hideNav={hideNavigation}
            settingsRoot={settingsRoot}
          />
        }
        showNewIntercomMessage={() => intercom.showNewMessage()}
      />
      <AlertContent />
      <VoiceWidget />
    </AiSlidePanelLayout>
  );
});
